import React, { useState, useEffect } from 'react';
import './AvatarModal.css';

interface AvatarModalProps {
  isOpen: boolean;
  onClose: () => void;
  avatarUrls: string[];
  userName?: string;
  userId: string | number;
}

const AvatarModal: React.FC<AvatarModalProps> = ({
  isOpen,
  onClose,
  avatarUrls,
  userName,
  userId
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    }
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : avatarUrls.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < avatarUrls.length - 1 ? prev + 1 : 0));
  };

  const currentAvatarUrl = avatarUrls[currentIndex] || null;

  return (
    <div 
      className="avatar-modal-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="avatar-modal-content">
        <button 
          className="avatar-modal-close"
          onClick={onClose}
          aria-label="Закрыть"
        >
          ×
        </button>
        
        <div className="avatar-modal-header">
          <h3>{userName || `Пользователь ${userId}`}</h3>
          {avatarUrls.length > 1 && (
            <div className="avatar-modal-counter">
              {currentIndex + 1} из {avatarUrls.length}
            </div>
          )}
        </div>
        
        <div className="avatar-modal-body">
          {avatarUrls.length > 1 && (
            <button 
              className="avatar-modal-nav avatar-modal-nav-prev"
              onClick={goToPrevious}
              aria-label="Предыдущее фото"
            >
              ‹
            </button>
          )}
          
          {currentAvatarUrl ? (
            <img 
              src={currentAvatarUrl} 
              alt={`Аватар ${userName || 'пользователя'} ${currentIndex + 1}`}
              className="avatar-modal-image"
            />
          ) : (
            <div className="avatar-modal-fallback">
              <div className="avatar-modal-fallback-icon">
                {userName ? userName.charAt(0).toUpperCase() : '?'}
              </div>
              <p>Фотография не найдена</p>
            </div>
          )}
          
          {avatarUrls.length > 1 && (
            <button 
              className="avatar-modal-nav avatar-modal-nav-next"
              onClick={goToNext}
              aria-label="Следующее фото"
            >
              ›
            </button>
          )}
        </div>
        
        {avatarUrls.length > 1 && (
          <div className="avatar-modal-thumbnails">
            {avatarUrls.map((url, index) => (
              <button
                key={index}
                className={`avatar-modal-thumbnail ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
              >
                <img 
                  src={url} 
                  alt={`Миниатюра ${index + 1}`}
                />
              </button>
            ))}
          </div>
        )}
        
        <div className="avatar-modal-footer">
          <p>ID пользователя: {userId}</p>
        </div>
      </div>
    </div>
  );
};

export default AvatarModal;
